import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TFunction } from 'i18next';
import { Report, ReportAttachment } from '../../types/report';
import FullBlockingModal from '../common/FullBlockingModal';
import { Button } from '../common/Button';
import { supabase } from '../../lib/supabase';
import { getReporterDisplay } from './reporterDisplay';

type AttachmentWithUrl = ReportAttachment & {
  signed_url: string | null;
};

interface ReportedModalInfoProps {
  report: Report;
  onClose: () => void;
}

const renderReportedInfo = (report: Report, t: TFunction) => {
  const reporterDisplay = getReporterDisplay(report, t);

  switch (report.content_type) {
    case 'profile':
      return (
        <div className="space-y-1">
          <p>
            <span className="font-semibold"> {reporterDisplay}</span>{' '}
            {t('has_reported.profile', { ns: 'reports' })}{' '}
            <span className="font-semibold">
              {report.reported?.first_name} {report.reported?.last_name}
            </span>
          </p>
        </div>
      );

    case 'job':
      return (
        <div className="space-y-1">
          <p>
            <span className="font-semibold"> {reporterDisplay}</span>{' '}
            {t('has_reported.job', { ns: 'reports' })}
          </p>
        </div>
      );

    case 'review':
      return (
        <div className="space-y-1">
          <p>
            <span className="font-semibold"> {reporterDisplay}</span>{' '}
            {t('has_reported.review', { ns: 'reports' })}
          </p>
        </div>
      );

    case 'quote':
      return (
        <div className="space-y-1">
          <p>
            <span className="font-semibold"> {reporterDisplay}</span>{' '}
            {t('has_reported.quote', { ns: 'reports' })}
          </p>
        </div>
      );

    case 'quote_request':
      return (
        <div className="space-y-1">
          <p>
            <span className="font-semibold"> {reporterDisplay}</span>{' '}
            {t('has_reported.quote_request', { ns: 'reports' })}
          </p>
        </div>
      );

    case 'issue':
      return (
        <div className="space-y-2">
          <p>
            <span className="font-semibold">{t('issue.reporter_label', { ns: 'reports' })}:</span>{' '}
            {reporterDisplay}
          </p>
          <p>
            <span className="font-semibold">
              {t('issue.reporter_email_label', { ns: 'reports' })}:
            </span>{' '}
            {report.reporter_email || '-'}
          </p>
          <p>
            <span className="font-semibold">{t('issue.page_label', { ns: 'reports' })}:</span>{' '}
            {report.page_url || '-'}
          </p>
          <p>
            <span className="font-semibold">{t('issue.title_label', { ns: 'reports' })}:</span>{' '}
            {report.issue_title || '-'}
          </p>
          <p>
            <span className="font-semibold">
              {t('issue.description_label', { ns: 'reports' })}:
            </span>{' '}
            {report.issue_description || report.details || '-'}
          </p>
        </div>
      );

    default:
      return <p className="text-sm">No information</p>;
  }
};

const ReportedModalInfo: React.FC<ReportedModalInfoProps> = ({ report, onClose }) => {
  const { t } = useTranslation(['reports', 'common']);
  const navigate = useNavigate();
  const [issueAttachments, setIssueAttachments] = useState<AttachmentWithUrl[]>([]);

  const attachments = useMemo(() => report.attachments ?? [], [report.attachments]);

  useEffect(() => {
    if (report.content_type !== 'issue' || attachments.length === 0) {
      setIssueAttachments([]);
      return;
    }

    const run = async () => {
      const withSignedUrls = await Promise.all(
        attachments.map(async attachment => {
          const { data, error } = await supabase.storage
            .from('issue-attachments')
            .createSignedUrl(attachment.path, 60 * 60);

          if (error) {
            return {
              ...attachment,
              signed_url: null,
            };
          }

          return {
            ...attachment,
            signed_url: data.signedUrl,
          };
        })
      );

      setIssueAttachments(withSignedUrls);
    };

    void run();
  }, [attachments, report.content_type]);

  const goToReportDetails = (currentReport: Report) => {
    if (!currentReport.entity_id) {
      return;
    }

    switch (currentReport.content_type) {
      case 'profile':
        navigate('/admin/users', { state: { selected_id: currentReport.entity_id } });
        break;

      case 'review':
        navigate('/admin/reviews', { state: currentReport.entity_id });
        break;

      case 'job':
        navigate('/admin/jobs', { state: { selected_id: currentReport.entity_id } });
        break;
    }
  };

  const canNavigateToEntity = ['profile', 'review', 'job'].includes(report.content_type);

  return (
    <FullBlockingModal innerClassName="md:max-w-[48rem]">
      <h3 className="text-xl font-semibold mt-4 mb-2">{t('report_info', { ns: 'reports' })}</h3>

      <div className="border border-gray-300 rounded-lg p-4 mb-6">
        {renderReportedInfo(report, t)}

        {report.content_type !== 'issue' && (
          <p className="my-2">
            {t('report_title', { ns: 'reports' })} :{' '}
            {t(`report_reasons.${report.reason}`, {
              ns: 'reports',
              defaultValue: report.reason,
            })}
          </p>
        )}

        {report.content_type === 'issue' && issueAttachments.length > 0 && (
          <div className="mt-3">
            <p className="font-semibold mb-2">{t('issue.attachments_label', { ns: 'reports' })}:</p>
            <ul className="space-y-2">
              {issueAttachments.map(attachment => (
                <li key={attachment.path}>
                  {attachment.signed_url ? (
                    <a
                      href={attachment.signed_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {attachment.file_name}
                    </a>
                  ) : (
                    <span className="text-gray-500">{attachment.file_name}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3">
        <Button onClick={onClose} variant="transparent-with-border">
          {t('common:form_actions.close', { ns: 'common' })}
        </Button>

        {canNavigateToEntity && (
          <Button
            onClick={() => {
              onClose();
              goToReportDetails(report);
            }}
          >
            {t('see', { ns: 'reports' })}{' '}
            {t(`content_type.${report.content_type}`, { ns: 'common' })}
          </Button>
        )}
      </div>
    </FullBlockingModal>
  );
};

export default ReportedModalInfo;
